<template>
  <div class="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-md">
      <div id="forgot-password-card" class="w-full">
          <!-- Logo -->
          <div class="flex justify-center mb-6">
            <div class="h-16 w-16 bg-primary-600 dark:bg-primary-500 rounded-xl flex items-center justify-center shadow-sm">
              <svg class="h-9 w-9 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V6C15 6.55228 14.5523 7 14 7H10C9.44772 7 9 6.55228 9 6V4Z"/>
                <rect x="10" y="7" width="4" height="8" rx="0.5" fill="currentColor"/>
                <circle cx="12" cy="17" r="1" fill="currentColor"/>
                <path d="M11 10L11.5 10.5L13 9" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Title Section -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {{ t('auth.forgotPassword.title') }}
            </h1>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ t('auth.forgotPassword.subtitle') }}
            </p>
          </div>
          <!-- Reset Password Form -->
          <HLForm v-if="!resetLinkSent" :model="{ email }" @submit.prevent="handleForgotPassword">
            <HLSpace vertical :size="20">
              <HLFormItem 
                :label="t('auth.forgotPassword.emailLabel')" 
                path="email" 
                :validation-status="error ? 'error' : undefined" 
                required
              >
                <HLInput
                  id="email"
                  v-model:modelValue="email"
                  type="email"
                  :placeholder="t('auth.forgotPassword.emailPlaceholder')"
                  size="lg"
                  autocomplete="email"
                  :disabled="loading"
                />
                <template v-if="error" #feedback>{{ error }}</template>
              </HLFormItem>

              <HLSpace vertical :size="16">
                <HLButton
                  id="forgot-password-submit"
                  color="blue"
                  variant="primary"
                  size="lg"
                  :disabled="loading"
                  :loading="loading"
                  block
                  @click="handleForgotPassword"
                >
                  {{ loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendResetLink') }}
                </HLButton>

                <div class="text-center">
                  <HLButton 
                    id="back-to-login-link" 
                    href="/login" 
                    variant="text" 
                    color="blue" 
                    size="sm"
                  >
                    {{ t('auth.forgotPassword.backToLogin') }}
                  </HLButton>
                </div>
              </HLSpace>
            </HLSpace>
          </HLForm>

          <!-- Success State -->
          <HLSpace v-else vertical :size="20">
            <!-- Success Alert -->
            <HLAlert
              type="success"
              :title="t('auth.forgotPassword.successTitle')"
              :description="t('auth.forgotPassword.successMessage', { email })"
              show-icon
            />

            <!-- Development Mode: Reset Link -->
            <HLAlert
              v-if="resetLink"
              type="info"
              :title="t('auth.forgotPassword.devModeTitle')"
              show-icon
            >
              <template #description>
                <HLSpace vertical :size="12">
                  <p class="text-sm">{{ t('auth.forgotPassword.devModeMessage') }}</p>
                  <HLButton
                    id="dev-reset-password-link"
                    :href="`/reset-password?token=${resetToken}`"
                    color="blue"
                    variant="primary"
                    size="md"
                  >
                    {{ t('auth.forgotPassword.resetPasswordButton') }}
                  </HLButton>
                </HLSpace>
              </template>
            </HLAlert>

            <!-- Additional Actions -->
            <HLSpace vertical :size="12" align="center" class="text-center">
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ t('auth.forgotPassword.emailNotReceived') }}
              </p>
              
              <HLSpace :size="8">
                <HLButton
                  id="try-again-btn"
                  color="blue"
                  variant="text"
                  size="sm"
                  @click="resetForm"
                >
                  {{ t('auth.forgotPassword.tryAgain') }}
                </HLButton>
                
                <span class="text-gray-300 dark:text-gray-600">|</span>
                
                <HLButton 
                  id="back-to-login-link-2" 
                  href="/login" 
                  variant="text" 
                  color="blue" 
                  size="sm"
                >
                  {{ t('auth.forgotPassword.backToLogin') }}
                </HLButton>
              </HLSpace>
            </HLSpace>
          </HLSpace>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { http } from '@/utils/httpClient'
import { useToast } from '@/composables/useToast'
import { 
  HLButton, 
  HLInput, 
  HLForm, 
  HLFormItem, 
  HLSpace, 
  HLAlert
} from '@platform-ui/highrise'
import { t } from '@/i18n/en-us'

const toast = useToast()

// Reactive data
const email = ref('')
const loading = ref(false)
const error = ref('')
const resetLinkSent = ref(false)
const resetLink = ref('')
const resetToken = ref('')

// Methods
const handleForgotPassword = async () => {
  error.value = ''
  
  if (!email.value) {
    error.value = 'Email is required'
    return
  }

  if (!email.value.includes('@')) {
    error.value = 'Please enter a valid email address'
    return
  }

  loading.value = true

  try {
    const response = await http.post('/auth/forgot-password', { 
      email: email.value 
    })
    
    resetLinkSent.value = true
    
    // In development, show the reset link
    if (response.resetLink) {
      resetLink.value = response.resetLink
      resetToken.value = response.resetToken
    }
    
    toast.success('Password reset link sent!')
  } catch (err) {
    console.error('Forgot password error:', err)
    error.value = err.message || 'Failed to send reset link. Please try again.'
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  email.value = ''
  error.value = ''
  resetLinkSent.value = false
  resetLink.value = ''
  resetToken.value = ''
}
</script>

